import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { Store } from '../../../services/store';
import { toVoid } from '../../../utils/void';
import { BomImportProvider } from './bom-import.provider';
import { BomImportFormatDto } from './models/bom-import-format.dto';
import { BomImportPreviewDto } from './models/bom-import-preview.dto';
import { MAX_ATTACHMENT_SIZE_BYTES } from '../bom-tree/components/bom-attachments/bom-attachments.store';

export interface BomImportState {
  loading: boolean;
  formats: BomImportFormatDto[] | undefined;
  selectedFormat: BomImportFormatDto | undefined;
  // the picked file stays until import or cancel — the import POSTs it again
  file: File | undefined;
  previewing: boolean;
  preview: BomImportPreviewDto | undefined;
  rootName: string;
  rootPartNumber: string;
  importing: boolean;
  // a 400's sentence, or the client-side size check; shown under the file picker or under the preview's buttons
  error: string | undefined;
}

const INITIAL_STATE: BomImportState = {
  loading: false,
  formats: undefined,
  selectedFormat: undefined,
  file: undefined,
  previewing: false,
  preview: undefined,
  rootName: '',
  rootPartNumber: '',
  importing: false,
  error: undefined
};

@Injectable({ providedIn: 'root' })
export class BomImportStore extends Store<BomImportState> {
  constructor(private provider: BomImportProvider) {
    super(INITIAL_STATE);
  }

  init(): Observable<void> {
    this.update(state => ({ ...state, loading: true }));

    return this.provider.getFormats().pipe(
      tap(formats => this.update(state => ({
        ...state,
        loading: false,
        formats,
        // one format today — preselected, so the control reads as a label with a caret
        selectedFormat: formats[0]
      }))),
      toVoid()
    );
  }

  selectFormat(format: BomImportFormatDto): void {
    this.update(state => ({ ...state, selectedFormat: format }));
  }

  // Choosing the file is the action: no separate button, the preview request goes out at once
  preview(file: File): Observable<void> {
    const format = this.state.selectedFormat;

    if (!format) {
      return of(undefined);
    }

    if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      this.update(state => ({ ...state, error: 'Plik przekracza limit 30 MB' }));
      return of(undefined);
    }

    this.update(state => ({ ...state, file, previewing: true, error: undefined }));

    return this.provider.preview(file, format.key).pipe(
      tap(preview => this.update(state => ({
        ...state,
        previewing: false,
        preview,
        rootName: preview.rootName,
        rootPartNumber: ''
      }))),
      catchError(error => {
        // a 400 shows its sentence in the same place and the page stays in the file-picking state
        this.update(state => ({ ...state, previewing: false, file: undefined, error: apiMessage(error, 'Nie udało się wczytać pliku') }));
        return of(undefined);
      }),
      toVoid()
    );
  }

  setRootName(rootName: string): void {
    this.update(state => ({ ...state, rootName }));
  }

  setRootPartNumber(rootPartNumber: string): void {
    this.update(state => ({ ...state, rootPartNumber }));
  }

  // Resolves with the new root's id, or undefined when the API refused (the sentence lands in error)
  import(): Observable<number | undefined> {
    const { file, selectedFormat, rootName, rootPartNumber } = this.state;

    if (!file || !selectedFormat || !rootName.trim()) {
      return of(undefined);
    }

    this.update(state => ({ ...state, importing: true, error: undefined }));

    return this.provider.import(file, selectedFormat.key, rootName.trim(), rootPartNumber.trim()).pipe(
      tap(() => this.update(state => ({ ...state, importing: false }))),
      map(result => result.rootBomItemId),
      catchError(error => {
        this.update(state => ({ ...state, importing: false, error: apiMessage(error, 'Nie udało się zaimportować pliku') }));
        return of(undefined);
      })
    );
  }

  cancel(): void {
    this.update(state => ({ ...state, file: undefined, preview: undefined, rootName: '', rootPartNumber: '', error: undefined }));
  }
}

function apiMessage(error: { error?: unknown }, fallback: string): string {
  return typeof error.error === 'string' ? error.error : fallback;
}
